import { ReactElement, useMemo } from 'react';
import { GridControlled } from '@customer/components/tables/grid/GridControlled';
import {
  AssetBasicModel,
  SiteAssetsListFilters,
  useListAssets,
} from '@customer/store/apis/assetsApi';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ConnectivitySnackbar } from '@dmc/ui';
import {
  columnDefAssetGroup,
  columnDefAssetName,
  columnDefCasSoftware,
  columnDefConfigGroupName,
  columnDefDeviceId,
  columnDefDeviceType,
  columnDefObjectType,
  columnDefSoftwareStatusExcludePpu,
} from '@customer/components/tables/grid/columnDefs';
import { ColDef } from 'ag-grid-community';
import { FilterGroup } from '@customer/filters/types';
import useFilters from '@customer/filters/useFilters';
import Page from '@customer/templates/page/Page';
import PageHeader from '@customer/components/layout/page-header/PageHeader';
import CardTable from '@customer/components/tables/card-table/CardTable';
import { QueryTag } from '@customer/store/api';
import { TagDescription } from '@reduxjs/toolkit/query';
import { useAbsoluteTimePartialColDef } from '@customer/components/tables/cells/absolute-time/useAbsoluteTime';

const AssetGridSliceKey = 'Assets';

const tagsToClearOnRefresh: TagDescription<QueryTag>[] = [
  { type: QueryTag.Asset, id: 'LIST' },
  QueryTag.ConfigGroup,
  { type: QueryTag.Metadata, id: 'VEHICLEGROUPS' },
];

export function AssetsList(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const absoluteTimeColDef = useAbsoluteTimePartialColDef<AssetBasicModel>();

  const columnDefs = useMemo<ColDef<AssetBasicModel>[]>(
    () => [
      { ...columnDefAssetName(t), maxWidth: 150 },
      { ...columnDefAssetGroup(t) },
      {
        field: 'ts',
        headerName: t('LastReportedTitleCase'),
        sort: 'desc',
        ...absoluteTimeColDef,
      },
      { ...columnDefConfigGroupName(t) },
      { ...columnDefObjectType(t) },
      { ...columnDefDeviceType(t), maxWidth: 200, suppressSizeToFit: false },
      { ...columnDefDeviceId(t) },
      { ...columnDefCasSoftware(t) },
      {
        ...columnDefSoftwareStatusExcludePpu(t),
        maxWidth: 200,
        suppressSizeToFit: false,
      },
    ],
    [t, absoluteTimeColDef],
  );

  const filters = useFilters<SiteAssetsListFilters>(FilterGroup.AssetsList);

  const { assets, hasData, error, noDataDescription, countAll, countFilter } = useListAssets({
    query: { ...filters },
  });

  const onRowClicked = (data: AssetBasicModel) => navigate(`/asset/${data.id}/details`);

  return (
    <>
      <Page
        fullWidth
        header={
          <PageHeader
            title={t('Assets')}
            sliceKey={AssetGridSliceKey}
            tagsToClearOnRefresh={tagsToClearOnRefresh}
            filterProps={{
              filterGroup: FilterGroup.AssetsList,
              countAll,
              countFilter,
              transKey: 'RegisteredAssets',
              filteredTransKey: 'ShowingFilteredRegisteredAssets',
            }}
          />
        }
      >
        <CardTable hasData={hasData} noDataDescription={noDataDescription}>
          <GridControlled<AssetBasicModel>
            sliceKey={AssetGridSliceKey}
            rowData={assets}
            columnDefs={columnDefs}
            onRowClick={onRowClicked}
          />
        </CardTable>
      </Page>
      <ConnectivitySnackbar error={error} message={t('FetchAssetsError')} title={t('Error')} />
    </>
  );
}

export default AssetsList;
